import type { User } from "@safers/api";

const SITE = "SITE";
const MENU = "MENU";
const ALL = "*";

export type MenuKey = "CONTROL" | "HEALTH" | "CCTV";

const siteIdsCache = new WeakMap<User, Set<string>>();
const menuIdsCache = new WeakMap<User, Set<string>>();

function idsOf(user: User, resourceType: string): Set<string> {
  const ids = new Set<string>();
  for (const p of user.permissions ?? []) {
    for (const rp of p.resources ?? []) {
      if (rp.resourceType === resourceType && rp.resourceId) ids.add(rp.resourceId);
    }
  }
  return ids;
}

export function getSiteIds(user: User | null): Set<string> {
  if (!user) return new Set<string>();
  const cached = siteIdsCache.get(user);
  if (cached) return cached;
  const ids = idsOf(user, SITE);
  siteIdsCache.set(user, ids);
  return ids;
}

export function getMenuKeys(user: User | null): Set<string> {
  if (!user) return new Set<string>();
  const cached = menuIdsCache.get(user);
  if (cached) return cached;
  const ids = idsOf(user, MENU);
  menuIdsCache.set(user, ids);
  return ids;
}

export function canAccessSite(user: User | null, siteId: string | number): boolean {
  if (!user) return false;
  if (user.authority === "ADMIN") return true;
  const ids = getSiteIds(user);
  return ids.has(ALL) || ids.has(String(siteId));
}

export function hasMenu(user: User | null, menu: MenuKey): boolean {
  if (!user) return false;
  if (user.authority === "ADMIN") return true;
  const keys = getMenuKeys(user);
  return keys.has(ALL) || keys.has(menu);
}

export function canAccessMenuSite(
  user: User | null,
  siteId: string | number,
  menu: MenuKey
): boolean {
  return hasMenu(user, menu) && canAccessSite(user, siteId);
}

export function getSoleSiteId(user: User | null): string | null {
  if (!user || user.authority === "ADMIN") return null;
  const ids = getSiteIds(user);
  if (ids.has(ALL) || ids.size !== 1) return null;
  return [...ids][0] ?? null;
}

export function hasNoSiteAccess(user: User | null): boolean {
  if (!user) return true;
  if (user.authority === "ADMIN") return false;
  const ids = getSiteIds(user);
  return !ids.has(ALL) && ids.size === 0;
}
